import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import { alpha, IconButton, InputBase, InputProps, styled } from '@mui/material'
import {
  Channels,
  dispatch,
  Events,
  Sources,
} from '@npm-client-frontends/client-frontends-custom-events'
import { ChangeEvent, useRef, useState } from 'react'

const darkBlue = '#20274d'

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: 'auto',

  // [theme.breakpoints.up('sm')]: {
  //   marginLeft: theme.spacing(1),
  //   width: `${0.25 * 100}em`,
  // },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    paddingRight: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shortest,
    }),
    width: '100%',
    fontWeight: 500,

    backgroundColor: darkBlue,
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.up('sm')]: {
      width: '25vw',
      '&:focus': {
        width: '30vw',
        backgroundColor: 'white',
        color: 'black',
        borderRadius: theme.shape.borderRadius,
      },
    },
  },
}))

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const CloseIconButton = styled(IconButton)(() => ({
  position: 'absolute',
  height: '100%',
  right: 0,
}))

type DebounceProps = {
  handleDebounce: (value: string) => void
  debounceTimeout: number
}

const DebounceInput = (props: InputProps & DebounceProps) => {
  const { handleDebounce, debounceTimeout, ...rest } = props

  const timerRef = useRef<number>()

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (props.onChange) {
      props.onChange(event)
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    timerRef.current = window.setTimeout(() => {
      handleDebounce(event.target.value)
    }, debounceTimeout)
  }

  return (
    <StyledInputBase
      {...rest}
      onChange={handleChange}
      inputProps={{ 'data-testid': 'search-input' }}
    />
  )
}

export const SearchInput = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  const handleDebounceSearchChange = (value: string) => {
    setDebouncedSearchTerm(value)
    dispatch(Channels.SEARCH, Sources.NAV, Events.SEARCH_TERM_CHANGE, value)
  }

  const handleClickReset = () => {
    setDebouncedSearchTerm('')
    setSearchTerm('')
    dispatch(Channels.SEARCH, Sources.NAV, Events.SEARCH_TERM_RESET)
  }

  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(event.target.value)

  return (
    <Search>
      <SearchIconWrapper>
        <SearchIcon
          sx={{ fill: isFocused ? 'black' : 'white', zIndex: 9999 }}
        />
      </SearchIconWrapper>
      <DebounceInput
        placeholder="Search…"
        inputProps={{ 'aria-label': 'search' }}
        value={searchTerm}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        debounceTimeout={500}
        handleDebounce={handleDebounceSearchChange}
      />
      {debouncedSearchTerm.length > 0 && (
        <CloseIconButton
          onClick={handleClickReset}
          edge="end"
          sx={{ mr: 0, color: 'black' }}
          data-testid="search-reset"
        >
          <CloseIcon />
        </CloseIconButton>
      )}
    </Search>
  )
}
